import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo } from 'react'

import { Button } from '@surein/ui'
import { useTranslation } from 'react-i18next'
import {
  StyledEllipsis,
  StyledPageSizeOptions,
  StyledPaginationButtons,
  StyledPaginationContainer,
} from './styles'
import { Row } from '@/components/styled'

interface TablePaginationProps {
  pageIndex: number
  pageCount: number
  onPageChange: (pageIndex: number) => void
  onPageSizeChange?: (pageSize: number) => void
  pageSizeOptions?: number[]
}

const generatePaginationPages = (
  pageIndex: number,
  pageCount: number,
): (number | string)[] => {
  const maxVisiblePages = 5
  const pages: (number | string)[] = []

  if (pageCount <= maxVisiblePages) {
    for (let i = 0; i < pageCount; i++) pages.push(i)
  } else {
    pages.push(0)

    if (pageIndex > 2) {
      pages.push('...')
    }

    const start = Math.max(1, pageIndex - 1)
    const end = Math.min(pageCount - 2, pageIndex + 1)

    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) pages.push(i)
    }

    if (pageIndex < pageCount - 3) {
      pages.push('...')
    }

    if (!pages.includes(pageCount - 1)) {
      pages.push(pageCount - 1)
    }
  }
  return pages
}

export const Pagination = ({
  pageIndex,
  pageCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
}: TablePaginationProps) => {
  const { t } = useTranslation()
  const pages = useMemo(() => generatePaginationPages(pageIndex, pageCount), [
    pageIndex,
    pageCount,
  ])

  const hasPreviousPage = pageIndex > 0
  const hasNextPage = pageIndex < pageCount - 1

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      onPageChange(pageIndex - 1)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      onPageChange(pageIndex + 1)
    }
  }

  return (
    <StyledPaginationContainer>
      <Row alignItems="center" gap="8px">
        <span>{t('pagination.display')}</span>
        <StyledPageSizeOptions>
          {pageSizeOptions.map((item) => (
            <Button
              key={item}
              variant="outline"
              size="sm"
              onClick={() => onPageSizeChange?.(item)}
            >
              {item}
            </Button>
          ))}
        </StyledPageSizeOptions>
        <span>{t('pagination.rows')}</span>
      </Row>
      <StyledPaginationButtons>
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePreviousPage}
          disabled={!hasPreviousPage}
        >
          <ChevronLeft size={16} />
        </Button>
        {pages.map((page, index) =>
          typeof page === 'string' ? (
            <StyledEllipsis key={`ellipsis-${index}`}>{page}</StyledEllipsis>
          ) : (
            <Button
              key={page}
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(page)}
            >
              {page + 1}
            </Button>
          ),
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNextPage}
          disabled={!hasNextPage}
        >
          <ChevronRight size={16} />
        </Button>
      </StyledPaginationButtons>
    </StyledPaginationContainer>
  )
}
